import axios from 'axios';
import { isMerchPreorder } from './merchFulfillmentLabels';
import { isOrderAlreadyCreated } from './orderCreation';

export const MERCH_CHECKOUT_SESSION_KEY = 'cil.merchCheckoutPending';

export function saveMerchCheckoutSession(payload) {
  try {
    sessionStorage.setItem(MERCH_CHECKOUT_SESSION_KEY, JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function readMerchCheckoutSession() {
  try {
    const raw = sessionStorage.getItem(MERCH_CHECKOUT_SESSION_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearMerchCheckoutSession() {
  try {
    sessionStorage.removeItem(MERCH_CHECKOUT_SESSION_KEY);
  } catch {
    // ignore
  }
}

export function buildMerchCheckoutSession({
  product,
  fulfillmentMethod,
  shippingAddress,
  pricing,
  selectedVariants,
  availableDiscount,
  participantId,
  localPaymentIntentId,
}) {
  const productId = product.documentId || product.id;
  return {
    productId,
    productName: product.nome,
    fulfillmentMethod,
    shippingAddress: fulfillmentMethod === 'shipping' ? shippingAddress : null,
    isPreorder: isMerchPreorder(product, 1),
    total: pricing.total,
    productTotal: pricing.productTotal,
    productSubtotal: pricing.productSubtotal,
    shippingFee: pricing.shippingFee,
    discountAmount: pricing.discountAmount,
    originalAmount: parseFloat(product.prezzo) || 0,
    selectedVariants: selectedVariants || {},
    discountId: availableDiscount?.id || null,
    discountPercentage: availableDiscount?.percentageAmount || null,
    participantId: participantId || null,
    localPaymentIntentId: localPaymentIntentId || '',
  };
}

export function buildMerchCheckoutSessionFromMetadata(
  metadata,
  { socioId, localPaymentIntentId, amountCents } = {}
) {
  if (!metadata?.productId) return null;

  let selectedVariants = metadata.variants || {};
  if (typeof selectedVariants === 'string') {
    try {
      selectedVariants = JSON.parse(selectedVariants);
    } catch {
      selectedVariants = {};
    }
  }

  let shippingAddress = metadata.shippingAddress || null;
  if (typeof shippingAddress === 'string') {
    try {
      shippingAddress = JSON.parse(shippingAddress);
    } catch {
      shippingAddress = null;
    }
  }

  const productTotal = parseFloat(metadata.productTotal) || 0;
  const shippingFee = parseFloat(metadata.shippingFee) || 0;
  const productSubtotal = parseFloat(metadata.productSubtotal) || 0;
  const discountAmount = parseFloat(metadata.discountAmount) || 0;
  const total =
    amountCents != null ? amountCents / 100 : productTotal + shippingFee;

  return {
    productId: metadata.productId,
    productName: metadata.productName,
    fulfillmentMethod: metadata.fulfillmentMethod || 'pickup',
    shippingAddress:
      metadata.fulfillmentMethod === 'shipping' ? shippingAddress : null,
    isPreorder: metadata.isPreorder === true || metadata.isPreorder === 'true',
    total,
    productTotal,
    productSubtotal,
    shippingFee,
    discountAmount,
    originalAmount:
      parseFloat(metadata.originalAmount) ||
      parseFloat(metadata.productPrice) ||
      productSubtotal,
    selectedVariants,
    discountId: metadata.discountId || null,
    discountPercentage: metadata.discountPercentage || null,
    participantId: socioId || null,
    localPaymentIntentId: localPaymentIntentId || '',
  };
}

export function buildMerchSuccessSummary({
  session,
  metadata,
  amountCents,
  paymentData = {},
}) {
  const meta = metadata || {};
  const shippingFee = parseFloat(meta.shippingFee ?? session?.shippingFee) || 0;
  const productTotal =
    parseFloat(meta.productTotal ?? session?.productTotal) || 0;
  const productSubtotal =
    parseFloat(meta.productSubtotal ?? session?.productSubtotal) || 0;
  const discountAmount =
    parseFloat(meta.discountAmount ?? session?.discountAmount) || 0;
  const originalAmount =
    parseFloat(meta.originalAmount ?? session?.originalAmount) || 0;
  const total =
    amountCents != null
      ? amountCents / 100
      : parseFloat(meta.total ?? session?.total) || productTotal + shippingFee;

  let shippingAddress =
    session?.shippingAddress ?? meta.shippingAddress ?? null;
  if (typeof shippingAddress === 'string') {
    try {
      shippingAddress = JSON.parse(shippingAddress);
    } catch {
      shippingAddress = null;
    }
  }

  const orderDocumentId =
    paymentData?.orderDocumentId ||
    meta.orderDocumentId ||
    meta.documentId ||
    null;

  return {
    ...paymentData,
    productId: session?.productId || meta.productId,
    productName: session?.productName || meta.productName,
    fulfillmentMethod:
      session?.fulfillmentMethod || meta.fulfillmentMethod || 'pickup',
    shippingAddress,
    isPreorder: session?.isPreorder === true || meta.isPreorder === true,
    total,
    productTotal,
    productSubtotal,
    originalAmount,
    shippingFee,
    discountAmount,
    selectedVariants: session?.selectedVariants || meta.selectedVariants || {},
    participantId: session?.participantId || meta.participantId || null,
    orderDocumentId,
    documentId: orderDocumentId,
    paymentIntentId:
      paymentData?.paymentIntentId || meta.paymentIntentId || null,
    localPaymentIntentId:
      paymentData?.localPaymentIntentId ||
      session?.localPaymentIntentId ||
      meta.localPaymentIntentId ||
      null,
    createdAt:
      paymentData?.createdAt || meta.createdAt || new Date().toISOString(),
  };
}

export function extractOrderDocumentId(orderResponse) {
  if (!orderResponse) return null;

  const payload = orderResponse.data ?? orderResponse;
  const order = payload?.order;

  if (order) {
    const orderData = order.data ?? order;
    const documentId =
      orderData?.documentId || orderData?.attributes?.documentId;
    if (documentId) return String(documentId);
  }

  if (payload?.documentId) return String(payload.documentId);

  return null;
}

function isMerchOrder(order, productId) {
  if (!order) return false;

  const hasMerchMetadata = Boolean(order.metadata?.merch);
  const hasProductRelation = Boolean(order.product);
  const hasProductLine = (order.metadata?.products || []).some(
    (product) => product?.type === 'product'
  );

  if (!hasMerchMetadata && !hasProductRelation && !hasProductLine) {
    return false;
  }

  if (!productId) return true;

  const orderProductId =
    order.product?.documentId ||
    order.product?.id ||
    order.metadata?.merch?.productId ||
    (order.metadata?.products || []).find(
      (product) => product?.type === 'product'
    )?.id;

  return String(orderProductId) === String(productId);
}

export function resolveMerchOrderDocumentId(summary = {}, userData = null) {
  const direct = String(
    summary.orderDocumentId || summary.documentId || ''
  ).trim();
  if (direct) return direct;

  const orders = userData?.orders || [];
  const { paymentIntentId, localPaymentIntentId, productId } = summary;

  if (paymentIntentId || localPaymentIntentId) {
    const paymentMatch = orders.find((order) => {
      if (paymentIntentId && order.paymentIntentId === paymentIntentId)
        return true;

      const paymentIntentRef = order.payment_intent;
      const linkedPaymentIntentId =
        paymentIntentRef?.documentId ||
        paymentIntentRef?.data?.documentId ||
        (typeof paymentIntentRef === 'string' ? paymentIntentRef : null);

      return Boolean(
        localPaymentIntentId && linkedPaymentIntentId === localPaymentIntentId
      );
    });

    if (paymentMatch?.documentId) {
      return String(paymentMatch.documentId);
    }
  }

  const merchOrders = orders.filter((order) => isMerchOrder(order, productId));
  if (merchOrders.length === 0) return null;

  merchOrders.sort(
    (a, b) =>
      new Date(b.createdAt || 0).getTime() -
      new Date(a.createdAt || 0).getTime()
  );

  return merchOrders[0]?.documentId ? String(merchOrders[0].documentId) : null;
}

export async function requestMerchOrderConfirmationEmail({
  orderDocumentId,
  localPaymentIntentId,
  paymentIntentId,
}) {
  return axios.post(
    '/api/ordini/merch/confirmation-email',
    {
      ...(orderDocumentId ? { orderDocumentId } : {}),
      ...(localPaymentIntentId ? { localPaymentIntentId } : {}),
      ...(paymentIntentId ? { paymentIntentId } : {}),
    },
    { withCredentials: true }
  );
}

export async function submitMerchOrder({
  productId,
  participantId,
  paymentIntentId,
  paymentStatus = 'succeeded',
  paymentMethodId,
  localPaymentIntentId,
  pricing,
  productName,
  originalAmount,
  availableDiscount,
  selectedVariants,
  fulfillmentMethod,
  shippingAddress,
}) {
  const response = await axios.post(
    '/api/ordini',
    {
      productId,
      participantId,
      paymentIntentId,
      paymentStatus,
      paymentMethod: 'elettronico',
      paymentMethodId: paymentMethodId || null,
      amount: pricing.total,
      originalAmount,
      discountId: availableDiscount?.id || null,
      discountPercentage: availableDiscount?.percentageAmount || null,
      discountAmount: pricing.discountAmount,
      shippingFee: pricing.shippingFee,
      productSubtotal: pricing.productSubtotal,
      paymentIntents: localPaymentIntentId,
      variants: selectedVariants,
      fulfillmentMethod,
      shippingAddress:
        fulfillmentMethod === 'shipping' ? shippingAddress : null,
      quantity: 1,
      eventVariants: {
        [productId]: {
          id: productId,
          name: productName,
          amount: pricing.productTotal,
          originalAmount,
          type: 'product',
          priceOption: 'totale',
          variants: selectedVariants,
          discount: availableDiscount
            ? {
                id: availableDiscount.id,
                percentageAmount: availableDiscount.percentageAmount,
                amount: pricing.discountAmount,
              }
            : null,
        },
      },
    },
    { withCredentials: true }
  );

  if (response.status === 200 || isOrderAlreadyCreated(response.data)) {
    return response;
  }

  throw new Error("Errore nella creazione dell'ordine");
}

export async function submitMerchOrderFromSession(session, paymentData) {
  if (!session?.productId) {
    throw new Error('Sessione checkout merch non valida');
  }

  const pricing = {
    total: session.total,
    productTotal: session.productTotal,
    productSubtotal: session.productSubtotal,
    shippingFee: session.shippingFee,
    discountAmount: session.discountAmount,
  };

  const availableDiscount =
    session.discountId && session.discountPercentage
      ? {
          id: session.discountId,
          percentageAmount: session.discountPercentage,
        }
      : null;

  try {
    return await submitMerchOrder({
      productId: session.productId,
      participantId: session.participantId,
      paymentIntentId: paymentData.paymentIntentId,
      paymentStatus: paymentData.paymentStatus || 'succeeded',
      paymentMethodId: paymentData.paymentMethodId,
      localPaymentIntentId:
        session.localPaymentIntentId || paymentData.localPaymentIntentId,
      pricing,
      productName: session.productName,
      originalAmount: session.originalAmount,
      availableDiscount,
      selectedVariants: session.selectedVariants || {},
      fulfillmentMethod: session.fulfillmentMethod,
      shippingAddress: session.shippingAddress,
    });
  } catch (err) {
    if (isOrderAlreadyCreated(err.response?.data)) {
      return err.response;
    }
    throw err;
  }
}
